import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';

import { useLazyGetEntityTreeQuery } from '@/components/orders/services/orderApi';
import { convertToTreeNode } from '@/services/entity-tree';
import { entityTreeLoaded } from '@/slice/entityScopeSlice';
import {
  useEntityTree,
  useParentEntityId,
  useSelectedEntityId,
} from '@/hooks/useEntityScope';
import type { EntityTreeNode } from '@/types/order.types';

/**
 * Entity tree for the header/nav TreeSelect, shared by the desktop header and
 * the mobile nav drawer.
 *
 * The slice is both this hook's output and its trigger. It fetches when the
 * parent anchor moves — and ALSO when the slice's tree has been cleared under
 * an unchanged parent, which is what a sub-entity switch (`scopeChanged`) and
 * a rename (`entityTreeLoaded(null)`) both do. Before DV-3013 PR 3 the
 * document reload covered those two cases; without it, the tree would be
 * nulled with nothing to refill it, `getEntityOptions()` — the property list's
 * entity filter — would go empty, and the header dropdown would keep showing a
 * stale copy out of this hook's own local state. Hence no local state.
 *
 * A FAILED fetch is the same hazard reached by a different route (PR #1150
 * review, finding 5): the reload used to retry it by remounting everything, and
 * the desktop header — unlike the mobile drawer — never unmounts. So a failure
 * re-opens the ask, and any scope move is a retry opportunity. Residual, stated
 * rather than papered over: a failure that is never followed by a scope move
 * stays empty until the next mount.
 */
export function useEntityTreeNodes(): EntityTreeNode[] {
  const [triggerEntityTree] = useLazyGetEntityTreeQuery();
  const dispatch = useDispatch();
  // The entity tree is keyed by parent entity. Read from the scope slice
  // rather than localStorage so a switch re-fetches instead of keeping the
  // previous account's tree (DV-3013 spec §2.1 class A).
  const parentId = useParentEntityId();
  const tree = useEntityTree();
  // Not read — a trigger. `entityId` is the only slice field a SUB-ENTITY
  // switch moves: it keeps the parent anchor, and `scopeChanged` nulls a tree
  // that a failed fetch has already left null. Without this the effect would
  // not re-run at all on the one event that should retry.
  const entityId = useSelectedEntityId();
  // Which parent this hook instance has an ANSWERED ask for. Not a
  // render-count optimisation: it is what distinguishes "the tree present in
  // the slice belongs to the previous parent" from "present and current", so
  // the guard below cannot be simplified to `if (tree) return` — that would
  // serve a stale tree after a parent change that did not clear it. Reset on
  // failure, because an ask that threw was never answered.
  const fetchedFor = useRef<string | null>(null);

  useEffect(() => {
    if (!parentId) return;
    if (fetchedFor.current === parentId && tree) return;
    fetchedFor.current = parentId;

    const getEntityTreeData = async () => {
      try {
        const fetched = await triggerEntityTree(parentId).unwrap();
        if (fetched) {
          // The persistence middleware writes localStorage; the slice is what
          // consumers subscribe to. Replaces the `entityTreeUpdated` window
          // event, which was a hand-rolled subscription to a storage write
          // (DV-3013 spec §2.6).
          dispatch(entityTreeLoaded(convertToTreeNode(fetched)));
        }
      } catch (error) {
        // Re-open the ask. Guarded on the parent still being the one we asked
        // for: a fetch that lost a race to a newer parent must not clear that
        // newer parent's marker and fetch it a second time.
        if (fetchedFor.current === parentId) fetchedFor.current = null;
        console.error('Error fetching entity tree:', error);
      }
    };
    getEntityTreeData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parentId, entityId, tree]);

  return tree ? [tree] : [];
}
